import { Entity, EntityType } from '~/types/Entity';
import { captureException } from '~/utils/sentry';

import { api } from '../createApi';
import { TagType } from '../TagType';

const CONVERSATION_MESSAGES_TAG = {
  type: TagType.ConversationalAi,
  id: 'MESSAGES',
};

const CONVERSATION_HISTORY_TAG = {
  type: TagType.ConversationalAi,
  id: 'CONVERSATION-HISTORY',
};

// Store pending conversations that need to be injected
const pendingConversations = new Map<string, any>();

export const clearPendingConversations = () => {
  pendingConversations.clear();
};

const updateConversationHistoryCaches = (
  dispatch: (action: unknown) => void,
  getState: () => any,
  updater: (draft: any) => void,
) => {
  const cachedArgs = api.util.selectCachedArgsForQuery(
    getState(),
    'getConversationHistory',
  );
  for (const args of cachedArgs) {
    dispatch(api.util.updateQueryData('getConversationHistory', args, updater));
  }
};

api.enhanceEndpoints({
  addTagTypes: [TagType.ConversationalAi as TagType],
  endpoints: {
    getConversations: {
      providesTags: (_result, _error, arg: Entity<EntityType>) => [
        {
          type: TagType.ConversationalAi,
          id: arg
            ? `CONVERSATION-${arg.type}-${arg.id}`
            : 'CONVERSATION-UNKNOWN',
        },
      ],
      async onQueryStarted(
        entity: Entity<EntityType>,
        { dispatch, queryFulfilled },
      ) {
        try {
          // Skip if entity is invalid
          if (!entity || !entity.type || !entity.id) {
            return;
          }

          const { data } = await queryFulfilled;
          const cacheKey = `${entity.type}-${entity.id}`;
          const pendingConversation = pendingConversations.get(cacheKey);

          // If there's a pending conversation for this entity, inject it
          if (pendingConversation && data) {
            dispatch(
              api.util.updateQueryData(
                'getConversations',
                entity,
                (draft: any) => {
                  if (draft && draft.items) {
                    // Check if conversation already exists (backend caught up)
                    const exists = draft.items.some(
                      (item: any) => item.id === pendingConversation.id,
                    );
                    if (!exists) {
                      draft.items.unshift(pendingConversation);
                      draft.total += 1;
                    } else {
                      // Backend has caught up, clear the pending conversation
                      pendingConversations.delete(cacheKey);
                    }
                  }
                },
              ),
            );
          }
        } catch (e) {
          captureException('Failed to update conversations with pending', e, {
            section: 'conversationalAi getConversations onQueryStarted',
          });
          clearPendingConversations();
        }
      },
    },
    getConversationHistory: {
      providesTags: [CONVERSATION_HISTORY_TAG],
    },
    getConversation: {
      providesTags: [CONVERSATION_MESSAGES_TAG],
      async onQueryStarted(
        { conversationId }: { conversationId: string },
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const { data } = await queryFulfilled;
          if (!data) return;

          const cachedArgs = api.util.selectCachedArgsForQuery(
            getState(),
            'getConversations',
          );
          for (const args of cachedArgs) {
            dispatch(
              api.util.updateQueryData(
                'getConversations',
                args,
                (draft: any) => {
                  const item = draft?.items?.find(
                    (i: { id: string }) => i.id === conversationId,
                  );
                  if (!item) return;
                  item.version = data.version;
                  if (data.title !== undefined) {
                    item.title = data.title;
                  }
                },
              ),
            );
          }

          updateConversationHistoryCaches(dispatch, getState, (draft: any) => {
            const item = draft?.items?.find(
              (i: { id: string }) => i.id === conversationId,
            );
            if (!item) return;
            item.version = data.version;
            if (data.title !== undefined) {
              item.title = data.title;
            }
          });
        } catch {
          // Cache sync is best-effort; rename refetches version before PATCH.
        }
      },
    },
    createConversation: {
      async onQueryStarted(
        entity: Entity<EntityType>,
        { dispatch, queryFulfilled },
      ) {
        // Skip if entity is invalid
        if (!entity || !entity.type || !entity.id) {
          return;
        }

        const { id: entityId, type: entityType } = entity;
        const cacheKey = `${entityType}-${entityId}`;

        try {
          const { data: newConversation } = await queryFulfilled;
          if (newConversation) {
            // Store the conversation as pending for this entity
            pendingConversations.set(cacheKey, newConversation);

            // Try to update the cache if it exists
            dispatch(
              api.util.updateQueryData(
                'getConversations',
                entity,
                (draft: any) => {
                  if (draft && draft.items) {
                    draft.items.unshift(newConversation);
                    draft.total += 1;
                  }
                },
              ),
            );

            dispatch(api.util.invalidateTags([CONVERSATION_HISTORY_TAG]));
          }
        } catch {
          // On error, invalidate the specific entity conversation tag
          dispatch(
            api.util.invalidateTags([
              {
                type: TagType.ConversationalAi,
                id: `CONVERSATION-${entityType}-${entityId}`,
              },
            ]),
          );
        }
      },
    },
    postUserMessage: {
      invalidatesTags: [CONVERSATION_MESSAGES_TAG],
    },
    patchConversation: {
      async onQueryStarted(
        { conversationId }: { conversationId: string },
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const { data: updated } = await queryFulfilled;
          if (!updated) return;

          const cachedArgs = api.util.selectCachedArgsForQuery(
            getState(),
            'getConversations',
          );
          for (const args of cachedArgs) {
            dispatch(
              api.util.updateQueryData(
                'getConversations',
                args,
                (draft: any) => {
                  const item = draft?.items?.find(
                    (i: { id: string }) => i.id === conversationId,
                  );
                  if (!item) return;
                  item.title = updated.title ?? null;
                  item.version = updated.version;
                },
              ),
            );
          }

          updateConversationHistoryCaches(dispatch, getState, (draft: any) => {
            const item = draft?.items?.find(
              (i: { id: string }) => i.id === conversationId,
            );
            if (!item) return;
            item.title = updated.title ?? null;
            item.version = updated.version;
          });
        } catch {
          // Cache sync is best-effort.
        }
      },
    },
    deleteConversation: {
      async onQueryStarted(
        { conversationId }: { conversationId: string },
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          await queryFulfilled;

          const cachedArgs = api.util.selectCachedArgsForQuery(
            getState(),
            'getConversations',
          );
          for (const args of cachedArgs) {
            dispatch(
              api.util.updateQueryData(
                'getConversations',
                args,
                (draft: any) => {
                  if (!draft?.items) return;
                  const index = draft.items.findIndex(
                    (item: { id: string }) => item.id === conversationId,
                  );
                  if (index === -1) return;
                  draft.items.splice(index, 1);
                  if (typeof draft.total === 'number') {
                    draft.total -= 1;
                  }
                },
              ),
            );
          }

          // Cursor pages cannot be patched in place — refetch so later
          // results fill the gap. See EKP-58354.
          dispatch(api.util.invalidateTags([CONVERSATION_HISTORY_TAG]));
        } catch {
          // Cache sync is best-effort.
        }
      },
    },
  },
});
